import { useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-tools-line',
    title: 'Platform Kurulur',
    description: 'İşletmenize özel, dönüşüm odaklı dijital satış platformu tasarlanır ve yapılandırılır. WhatsApp entegrasyonu, form sistemi ve funnel yapısı hazırlanır.',
    result: null as string | null,
    color: 'text-gold',
    bg: 'bg-gold/10',
    border: 'border-gold/20',
    accent: 'bg-gold',
  },
  {
    number: '02',
    icon: 'ri-links-line',
    title: 'Reklam Bağlanır',
    description: 'Google Ads, Meta Ads veya diğer reklam platformları platformunuza bağlanır. Her kampanya için özel sayfalar oluşturulur. Bütçeniz artık boşa gitmiyor.',
    result: null as string | null,
    color: 'text-neon',
    bg: 'bg-neon/10',
    border: 'border-neon/20',
    accent: 'bg-neon',
  },
  {
    number: '03',
    icon: 'ri-user-received-2-line',
    title: 'Müşteriler Gelmeye Başlar',
    description: 'Sistem devreye girdikten itibaren, reklamdan gelen her ziyaretçi otomatik olarak yönlendirilir ve size ulaşır. Müşteriler akmaya başlar.',
    result: 'Müşteriler size yazmaya başlar',
    color: 'text-green-400',
    bg: 'bg-green-400/10',
    border: 'border-green-400/20',
    accent: 'bg-green-400',
  },
];

export default function HowItWorksSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.step-card').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 200);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="py-24 md:py-32 relative overflow-hidden" style={{ background: '#0d0d15' }}>
      <div className="absolute top-0 left-0 right-0 section-divider" />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 80% 50%, #00d4ff, transparent 60%)',
      }} />

      <div className="max-w-5xl mx-auto px-4 md:px-8" ref={ref}>
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-neon/10 border border-neon/20 px-4 py-2 rounded-full mb-5">
            <i className="ri-route-line text-neon text-sm" />
            <span className="text-neon text-sm font-semibold">Nasıl Çalışır</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight">
            3 Adımda{' '}
            <span className="text-gold-gradient">Müşteri Makineniz</span>{' '}
            Hazır
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Kurulumdan ilk müşteriye kadar her şeyi biz yönetiyoruz.
          </p>
        </div>

        {/* Step Cards */}
        <div className="space-y-6">
          {steps.map((step, idx) => (
            <div
              key={step.number}
              className="step-card glass rounded-2xl p-7 md:p-8 relative overflow-hidden card-hover"
              style={{
                opacity: 0,
                transform: 'translateY(30px)',
                transition: 'all 0.5s ease',
                border: `1px solid rgba(255,255,255,0.05)`,
              }}
            >
              {/* Number background */}
              <div
                className="absolute right-6 top-1/2 -translate-y-1/2 font-black text-8xl md:text-9xl opacity-[0.04] select-none"
                style={{ color: idx === 0 ? '#f0b429' : idx === 1 ? '#00d4ff' : '#4ade80' }}
              >
                {step.number}
              </div>

              <div className="flex flex-col md:flex-row items-start md:items-center gap-6 relative z-10">
                {/* Icon */}
                <div className={`w-16 h-16 flex items-center justify-center rounded-2xl ${step.bg} border ${step.border} flex-shrink-0`}>
                  <i className={`${step.icon} text-3xl ${step.color}`} />
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-xs font-black ${step.color} opacity-60 tracking-widest`}>ADIM {step.number}</span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-extrabold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed max-w-2xl">{step.description}</p>

                  {/* Result highlight — sadece sonuç varsa göster */}
                  {step.result && (
                    <div className="mt-4 inline-flex items-center gap-2.5 rounded-xl px-4 py-2.5 border border-green-400/25"
                      style={{ background: 'rgba(74,222,128,0.08)' }}>
                      <span className="relative flex items-center justify-center w-2.5 h-2.5 flex-shrink-0">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
                      </span>
                      <span className="text-green-400 font-bold text-sm tracking-wide">
                        {step.result}
                      </span>
                      <i className="ri-message-2-line text-green-400 text-sm" />
                    </div>
                  )}
                </div>

                {/* Right badge */}
                <div className={`hidden lg:flex w-10 h-10 items-center justify-center rounded-full ${step.accent} flex-shrink-0`}>
                  <i className="ri-check-line text-dark font-black text-lg" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center">
          <div className="glass-gold rounded-2xl p-8 max-w-sm mx-auto">
            <p className="text-white font-semibold text-lg mb-2">Başlamak için hazır mısınız?</p>
            <p className="text-gray-400 text-sm">Kurulum <strong className="text-gold">24 saat</strong> içinde tamamlanıyor.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
